import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";
import { Helmet } from "react-helmet";

import { BlogPostEntity } from "@/mk2/apps/blogs/schemas";
import { counsellingsNewUrl } from "@/mk2/apps/counsellings/urls";
import { CollapsedTextBox } from "@/mk2/apps/strollers/components/CollapsedTextBox";
import { MoreStrollers } from "@/mk2/apps/strollers/components/MoreStrollers";
import { StrollerVariantConfigurator } from "@/mk2/apps/strollers/components/StrollerVariantConfigurator";
import { StrollersBuyingGuides } from "@/mk2/apps/strollers/components/StrollersBuyingGuides";
import { dummyPhoto } from "@/mk2/apps/strollers/constants";
import { FeaturesList } from "@/mk2/apps/strollers/containers/StrollersDetail/FeaturesList";
import { LinkedArticles } from "@/mk2/apps/strollers/containers/StrollersDetail/LinkedArticles";
import { LinkedObjects } from "@/mk2/apps/strollers/containers/StrollersDetail/LinkedObjects";
import { MainFeaturesPills } from "@/mk2/apps/strollers/containers/StrollersDetail/MainFeaturesPills";
import { QuestionsAndAnswers } from "@/mk2/apps/strollers/containers/StrollersDetail/QuestionsAndAnswers";
import { SimilarStrollers } from "@/mk2/apps/strollers/containers/StrollersDetail/SimilarStrollers";
import StrollerDetailDescription from "@/mk2/apps/strollers/containers/StrollersDetail/StrollerDetailDescription";
import { StrollerDetailHeadlineAndBadges } from "@/mk2/apps/strollers/containers/StrollersDetail/StrollerDetailHeadlineAndBadges";
import { StrollerDetailRating } from "@/mk2/apps/strollers/containers/StrollersDetail/StrollerDetailRating";
import { StrollerDetailReviews } from "@/mk2/apps/strollers/containers/StrollersDetail/StrollerDetailReviews";
import { StrollerDetailWhereToBuy } from "@/mk2/apps/strollers/containers/StrollersDetail/StrollerDetailWhereToBuy";
import { StrollerVideo } from "@/mk2/apps/strollers/containers/StrollersDetail/StrollersDetail.constants";
import { Props } from "@/mk2/apps/strollers/containers/StrollersDetail/StrollersDetailPage";
import { getProductIsNoLongerSoldLabel } from "@/mk2/apps/strollers/helpers";
import {
    strollersDetailAlternateUrl,
    strollersDetailUrl,
    strollersListUrl,
    strollersPhotoUrl,
    strollersPhotosGalleryUrl,
    strollersUrl,
    strollersVariantPhotoUrl,
    strollersVariantPhotosGalleryUrl,
} from "@/mk2/apps/strollers/urls";
import AnnouncementBox, { BoxType } from "@/mk2/components/AnnouncementBox";
import { STROLLERS_GUIDE_SECTION_HASHTAGS } from "@/mk2/components/Badge";
import { BreadcrumbSiteStructure } from "@/mk2/components/BreadcrumbSiteStructure";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { Failure } from "@/mk2/components/errors/Failure";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { makeAbsoluteUrl, url } from "@/mk2/helpers/urls";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import type { HistoryLocationState } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import {
    BACK_TO_STROLLERS_HOMEPAGE,
    STROLLERS_DETAILED_REVIEW,
    STROLLERS_DETAIL_MORE_STROLLERS_OF_THIS_brand,
    STROLLER_ADD_QUESTION,
    STROLLER_AT_OFFICIAL_PAGE_OF_name,
    STROLLER_DETAIL_ARTICLES_WITH_stroller,
    STROLLER_DETAIL_DESCRIPTION,
    STROLLER_DETAIL_LINKED_OBJECTS,
    STROLLER_DETAIL_MORE_ARTICLES_WITH_stroller,
    STROLLER_DETAIL_SHOW_AVAILABLE_VARIANTS,
    STROLLER_FEATURES,
    STROLLER_HAS_BEEN_DELETED,
    STROLLER_MORE_DESCRIPTION,
    STROLLER_MORE_INFO_AT_link,
    STROLLER_STROLLEROPEDIA,
    STROLLER_UNPREPARED_TAG,
} from "@/mk/autogenerated/translation";
import { getYoutubeIframe } from "@/mk/bazaar/common/filters/youtubize";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

import styles from "./StrollersDetail.mscss";

const fasTrash: FontAwesomeIconProps = { icon: faTrash };

interface State {
    showRecommendedChoiceDesc: boolean;
    currentVariantId: number;
    windowWidth: number;
}

export class StrollersDetail extends React.Component<Props, State> {
    static getDerivedStateFromProps(props, state) {
        if (!(state.currentVariantId in props.variantsOptions)) {
            if (props.variantId) {
                return {
                    currentVariantId: props.variantId,
                };
            } else {
                return {
                    currentVariantId: props.baseVariantId,
                };
            }
        }
        return null;
    }
    public state: State = {
        showRecommendedChoiceDesc: false,
        currentVariantId: this.props.variantId ? this.props.variantId : this.props.baseVariantId,
        windowWidth: 375,
    };

    updateVariant = (id) => {
        this.setState({ currentVariantId: id });
    };

    updateWidth = () => {
        if (typeof window !== "undefined") {
            const newWidth = window.innerWidth <= 624 ? window.innerWidth : 624;
            this.setState({ windowWidth: newWidth });
        }
    };

    public componentDidMount() {
        const { loadingState, stroller, requestUser, isAdmin, location } = this.props;

        if (isSuccess(loadingState)) {
            window.addEventListener("resize", this.updateWidth);

            this.props.onStoreEvent("stroller_detail_view", {
                stroller_id: stroller.id,
                stroller_name: stroller.name,
                stroller_slug: stroller.slug,
                brand_id: stroller.brand.id,
                brand_name: stroller.brand.name,
                brand_slug: stroller.brand.slug,
                is_admin: isAdmin,
                page_ref: typeof window !== "undefined" ? window.document.referrer : "",
                pwa_ref: (location.state as HistoryLocationState)?.UNSAFE_mkGoBackUrl || "",

                // Facebook Conversions API event's content data
                fbc_props_json: isAuthenticated(requestUser)
                    ? JSON.stringify({
                          content_type: "product",
                          content_category: "MK",
                          content_name: "kociky",
                      })
                    : "",
            });
        }
    }

    public componentDidUpdate(prevProps: Props, prevState) {
        const oldSlug = prevProps.match.params.slug;
        const newSlug = this.props.match.params.slug;

        if (newSlug !== oldSlug) {
            this.setState({ showRecommendedChoiceDesc: false });
        }

        if (!isSuccess(prevProps.loadingState) && isSuccess(this.props.loadingState)) {
            // When switching from loading to loaded - trigger view event
            const { stroller, requestUser, isAdmin, location } = this.props;

            window.addEventListener("resize", this.updateWidth);

            this.props.onStoreEvent("stroller_detail_view", {
                stroller_id: stroller.id,
                stroller_name: stroller.name,
                stroller_slug: stroller.slug,
                brand_id: stroller.brand.id,
                brand_name: stroller.brand.name,
                brand_slug: stroller.brand.slug,
                is_admin: isAdmin,
                page_ref: typeof window !== "undefined" ? window.document.referrer : "",
                pwa_ref: (location.state as HistoryLocationState)?.UNSAFE_mkGoBackUrl || "",

                // Facebook Conversions API event's content data
                fbc_props_json: isAuthenticated(requestUser)
                    ? JSON.stringify({
                          content_type: "product",
                          content_category: "MK",
                          content_name: "kociky",
                      })
                    : "",
            });
        }
    }

    public render() {
        const { loadingState, stroller } = this.props;

        return (
            <div className={styles.StrollersDetail}>
                <BreadcrumbSiteStructure
                    items={this.getBreadcrumbItems()}
                    className={styles.StrollersDetail__breadcrumb}
                />

                {stroller && (
                    <Helmet>
                        {stroller.siblingSlugs &&
                            Object.keys(stroller.siblingSlugs).map((key) => {
                                const k = parseInt(key, 10);
                                const serverId: 201 | 202 =
                                    k === 201 ? 201 : k === 202 ? 202 : null; // odfiltruj sb.com
                                return !!serverId && serverId !== ACTIVE_SERVER_ID ? (
                                    <link
                                        rel="alternate"
                                        href={makeAbsoluteUrl(
                                            url(strollersDetailAlternateUrl(serverId), {
                                                slug: stroller.slug,
                                            }),
                                            serverId,
                                        )}
                                        hrefLang={`${{ 201: "sk-sk", 202: "cs-cz" }[serverId]}`}
                                        key={serverId}
                                    />
                                ) : null;
                            })}
                    </Helmet>
                )}

                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderSuccess={this.renderLoadSuccess}
                    onRenderFailure={this.renderFailure}
                    hasData={!!stroller}
                />
            </div>
        );
    }

    private renderLoadSuccess = () => {
        const {
            onComparisonToggle,
            stroller,
            strollerPhotos,
            relatedStrollersPreviewPhotos,
            strollerVariantsPhotosMap,
            variantsPhotoIds,
            variants,
            variantsOptions,
            strollerQuestions,
            patterns,
            strollerReviews,
            strollerPendingReviews,
            strollerAlikeStrollers,
            comparison,
            similars,
            strollersOfThisBrand,
            loadMoreSimilarsLoadingState,
            hasMoreSimilars,
            countOfSimilars,
            oldProductsCount,
            cheapestOldProduct,
            descriptionAST,
            descriptionASTAdditionalData,
            descriptionPhotos,

            allEshopProducts,

            buyingGuide,
            linkedObjects,
            counselling,
            articles,
            onChangeVariantFeatureValue,
            onStoreEvent,

            isAdmin,
            isMobile,
        } = this.props;

        const productIsNoLongerSold = getProductIsNoLongerSoldLabel(stroller);
        const propagateSimilarProducts = productIsNoLongerSold && !cheapestOldProduct;
        const currentVariantId = this.state.currentVariantId;

        const allVideos = stroller.features.video_url
            ? stroller.features.video_url.map((item: StrollerVideo) => {
                  if (item.type === "youtube") {
                      return (
                          <div className={cx(styles.StrollersDetail__video)} key={item.code}>
                              {getYoutubeIframe(item.code, 620, 348)}
                          </div>
                      );
                  }
              })
            : null;

        let photos = [];

        if (!!stroller && !!variantsPhotoIds[this.state.currentVariantId]) {
            variantsPhotoIds[this.state.currentVariantId].map(
                (photoId) =>
                    photoId in strollerVariantsPhotosMap &&
                    photos.push({ ...strollerVariantsPhotosMap[photoId] }),
            );
        } else {
            photos = strollerPhotos;
        }
        const firstPhoto = photos[0];
        const thumbnailPhotos = photos.length > 0 ? photos.slice(1, 7) : [];
        const morePhotosLength = photos.length - 2;

        const ifAnyDetailedReview = (returnBool: boolean) => (article: BlogPostEntity) => {
            if (
                article.hashtagItems.find(
                    (value) =>
                        value.hashtag.label === STROLLERS_GUIDE_SECTION_HASHTAGS.RedactionReview,
                )
            ) {
                return returnBool;
            }
            return !returnBool;
        };
        const articlesDetailedReviews = articles.filter(ifAnyDetailedReview(true));
        const articlesExceptDetailedReviews = articles.filter(ifAnyDetailedReview(false));

        return (
            <div>
                {!!stroller.deletedAt && (
                    <AnnouncementBox
                        message={STROLLER_HAS_BEEN_DELETED}
                        hasDetailMessage={false}
                        icon={fasTrash}
                        type={BoxType.Deleted}
                    />
                )}

                <div className={styles.StrollersDetail__UserRating}>
                    <StrollerDetailRating stroller={stroller} reviews={strollerReviews} />
                </div>

                <StrollerDetailHeadlineAndBadges stroller={stroller} />

                <div className={styles.StrollersDetail__gallery}>
                    <div className={styles.StrollersDetail__gallery__main}>
                        {photos.length > 0 ? (
                            <Link
                                to={
                                    currentVariantId
                                        ? url(strollersVariantPhotoUrl, {
                                              slug: stroller.slug,
                                              variantId: currentVariantId,
                                              photoId: firstPhoto.id,
                                          })
                                        : url(strollersPhotoUrl, {
                                              slug: stroller.slug,
                                              photoId: firstPhoto.id,
                                          })
                                }
                            >
                                <ImgCropped
                                    imgUrl={
                                        "photo" in firstPhoto
                                            ? firstPhoto?.photo.url720x720
                                            : firstPhoto?.url720x720
                                    }
                                    lazy={false}
                                    imgWidth={
                                        "photo" in firstPhoto
                                            ? firstPhoto?.photo.originalWidth
                                            : firstPhoto?.originalWidth
                                    }
                                    imgHeight={
                                        "photo" in firstPhoto
                                            ? firstPhoto?.photo.originalHeight
                                            : firstPhoto?.originalHeight
                                    }
                                    mode={ImgCropMode.Embed}
                                    width={this.state.windowWidth}
                                    height={(this.state.windowWidth / 16) * 9}
                                />
                            </Link>
                        ) : (
                            <ImgCropped
                                imgUrl={dummyPhoto.url250x250}
                                lazy={false}
                                imgWidth={dummyPhoto.originalWidth}
                                imgHeight={dummyPhoto.originalHeight}
                                mode={ImgCropMode.Embed}
                                width={250}
                                height={250}
                            />
                        )}
                    </div>

                    {/* NOTE: <PhotosRow /> do the same but need PhotoFile instead ImageGalleryItem */}
                    <div className={styles.StrollersDetail__gallery__thumbnails}>
                        {thumbnailPhotos.map((photo, index) => (
                            <div
                                key={photo?.id}
                                className={cx(
                                    styles.StrollersDetail__gallery__thumbnails__thumbnail,
                                    styles[
                                        `StrollersDetail__gallery__thumbnails__thumbnail--idx${index}`
                                    ],
                                    morePhotosLength - index > 0 &&
                                        styles[
                                            `StrollersDetail__gallery__thumbnails__thumbnail--idx${index}--hasMore`
                                        ],
                                )}
                            >
                                <Link
                                    to={
                                        currentVariantId
                                            ? url(strollersVariantPhotoUrl, {
                                                  slug: stroller.slug,
                                                  variantId: currentVariantId,
                                                  photoId: photo.id,
                                              })
                                            : url(strollersPhotoUrl, {
                                                  slug: stroller.slug,
                                                  photoId: photo.id,
                                              })
                                    }
                                >
                                    <ImgCropped
                                        imgUrl={
                                            "photo" in photo
                                                ? photo?.photo.url720x720
                                                : photo?.url720x720
                                        }
                                        lazy={false}
                                        imgWidth={
                                            "photo" in photo
                                                ? photo?.photo.originalWidth
                                                : photo?.originalWidth
                                        }
                                        imgHeight={
                                            "photo" in photo
                                                ? photo?.photo.originalHeight
                                                : photo?.originalHeight
                                        }
                                        mode={ImgCropMode.Embed}
                                        width={64}
                                        height={64}
                                    />
                                </Link>
                                {morePhotosLength - index > 0 && (
                                    <Link
                                        to={
                                            currentVariantId
                                                ? url(strollersVariantPhotosGalleryUrl, {
                                                      slug: stroller.slug,
                                                      variantId: currentVariantId,
                                                  })
                                                : url(strollersPhotosGalleryUrl, {
                                                      slug: stroller.slug,
                                                  })
                                        }
                                        key={photo?.id}
                                        className={cx(
                                            styles.StrollersDetail__gallery__thumbnails__thumbnail__more,
                                            styles[
                                                `StrollersDetail__gallery__thumbnails__thumbnail--idx${index}__more`
                                            ],
                                        )}
                                    >
                                        {" "}
                                        +{morePhotosLength - index + 1}
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {(!stroller.prepared || productIsNoLongerSold) && (
                    <div className={styles.StrollersDetail__tags}>
                        {!stroller.prepared && (
                            <span className={styles.StrollersDetail__tags__unpreparedTag}>
                                {STROLLER_UNPREPARED_TAG}
                            </span>
                        )}
                        {productIsNoLongerSold && (
                            <span
                                className={styles.StrollersDetail__tags__productIsNoLongerSoldTag}
                            >
                                {productIsNoLongerSold}
                            </span>
                        )}
                    </div>
                )}

                {!(this.state.currentVariantId in variantsOptions) &&
                    !!stroller.minPricedEshopProduct && (
                        <div className={styles.StrollersDetail__showVariants}>
                            <Btn
                                className={styles.StrollersDetail__showVariants__btn}
                                label={STROLLER_DETAIL_SHOW_AVAILABLE_VARIANTS}
                                type={BtnType.Blue}
                                link={url(strollersDetailUrl, { slug: stroller.slug })}
                            />
                        </div>
                    )}

                {stroller.configurables && (
                    <StrollerVariantConfigurator
                        stroller={stroller}
                        strollerPhotosMap={strollerVariantsPhotosMap}
                        variants={variants}
                        currentVariantId={this.state.currentVariantId}
                        variantsOptions={variantsOptions}
                        eshopProducts={allEshopProducts}
                        patterns={patterns}
                        onChangeVariantFeatureValue={onChangeVariantFeatureValue}
                        changeVariant={this.updateVariant}
                    />
                )}

                {isMobile && buyingGuide && (
                    <StrollersBuyingGuides
                        className={styles.StrollersDetail__buyingGuides}
                        buyingGuideContentItem={buyingGuide}
                    />
                )}

                {!stroller.deletedAt && <MainFeaturesPills stroller={stroller} />}

                <div className={styles.StrollersDetail__description}>
                    <h2 className={styles.StrollersDetail__description__headline}>
                        {STROLLER_DETAIL_DESCRIPTION}
                    </h2>

                    <CollapsedTextBox height={200} expandText={STROLLER_MORE_DESCRIPTION}>
                        <StrollerDetailDescription
                            stroller={stroller}
                            description={descriptionAST}
                            data={descriptionASTAdditionalData}
                            photos={descriptionPhotos}
                            isMobile={isMobile}
                        />
                        {stroller.features.model_url && (
                            <div className={styles.StrollersDetail__moreInfoAt}>
                                <Interpolate
                                    i18nKey={STROLLER_MORE_INFO_AT_link}
                                    link={
                                        <a
                                            data-google-interstitial="false"
                                            href={stroller.features.model_url}
                                            target="_blank"
                                            rel="noopener"
                                        >
                                            {interpolate(STROLLER_AT_OFFICIAL_PAGE_OF_name, {
                                                name: stroller.name,
                                            })}
                                        </a>
                                    }
                                />
                            </div>
                        )}
                    </CollapsedTextBox>
                </div>

                {!stroller.deletedAt && !!allVideos && allVideos.length > 0 && <>{allVideos}</>}

                {!stroller.deletedAt && (
                    <div className={styles.StrollersDetail__features}>
                        <h2 className={styles.StrollersDetail__features__headline}>
                            {STROLLER_FEATURES}
                        </h2>
                        <FeaturesList
                            brand={stroller.brand}
                            features={stroller.features}
                            variantAttributes={stroller.variantAttributes}
                        />
                    </div>
                )}

                {propagateSimilarProducts && (
                    <SimilarStrollers
                        className={styles.StrollersDetail__similarStrollers}
                        similars={similars}
                        comparison={comparison}
                        onComparisonToggle={onComparisonToggle}
                        onLoadMore={this.onLoadMoreSimilars}
                        loadingState={loadMoreSimilarsLoadingState}
                        hasMore={hasMoreSimilars}
                        count={countOfSimilars}
                        strollerPreviewPhotoMap={relatedStrollersPreviewPhotos}
                        photoObjectsMap={strollerVariantsPhotosMap}
                    />
                )}

                {articles.length > 0 && (
                    <>
                        {articlesDetailedReviews.length > 0 && (
                            <LinkedArticles
                                className={cx(
                                    styles.StrollersDetail__linkedArticles,
                                    styles["StrollersDetail__linkedArticles--detailedReview"],
                                )}
                                header={STROLLERS_DETAILED_REVIEW}
                                articles={articlesDetailedReviews}
                            />
                        )}
                        {articlesExceptDetailedReviews.length > 0 && (
                            <LinkedArticles
                                className={styles.StrollersDetail__linkedArticles}
                                header={interpolate(
                                    articlesDetailedReviews.length
                                        ? STROLLER_DETAIL_MORE_ARTICLES_WITH_stroller
                                        : STROLLER_DETAIL_ARTICLES_WITH_stroller,
                                    { stroller: stroller.name },
                                )}
                                articles={articlesExceptDetailedReviews}
                            />
                        )}
                    </>
                )}

                {!stroller.deletedAt && (
                    <StrollerDetailReviews
                        className={styles.StrollersDetail__userReviews}
                        stroller={stroller}
                        reviews={strollerReviews}
                        pendingReviews={strollerPendingReviews}
                        alikeStrollers={strollerAlikeStrollers}
                    />
                )}

                {!stroller.deletedAt && (
                    <QuestionsAndAnswers
                        className={styles.StrollersDetail__questionsAndAnswers}
                        questions={strollerQuestions}
                        addButton={
                            // TODO: https://trello.com/c/IzP0jm7C
                            /* counselling ? (
                                <Btn
                                    label={STROLLER_ADD_QUESTION}
                                    link={url(counsellingsNewUrl, { topicSlug: counselling.slug })}
                                />
                            ) :*/ null
                        }
                    />
                )}

                {!stroller.deletedAt && (
                    <StrollerDetailWhereToBuy
                        className={styles.StrollersDetail__whereToBuy}
                        stroller={stroller}
                        cheapestOldProduct={cheapestOldProduct}
                        oldProductsCount={oldProductsCount}
                        isMobile={isMobile}
                    />
                )}

                {linkedObjects.length > 0 && !stroller.deletedAt && (
                    <LinkedObjects
                        className={styles.StrollersDetail__linkedObjects}
                        header={STROLLER_DETAIL_LINKED_OBJECTS}
                        linkedObjects={linkedObjects}
                        addButton={
                            counselling ? (
                                <Btn
                                    label={STROLLER_ADD_QUESTION}
                                    link={url(counsellingsNewUrl, { topicSlug: counselling.slug })}
                                />
                            ) : null
                        }
                    />
                )}

                {Boolean(strollersOfThisBrand?.length) && (
                    <div className={styles.StrollersDetail__moreStrollersOfThisBrand}>
                        <h2 className={styles.StrollersDetail__moreStrollersOfThisBrand__headline}>
                            {interpolate(STROLLERS_DETAIL_MORE_STROLLERS_OF_THIS_brand, {
                                brand: stroller.brand.name,
                            })}
                        </h2>
                        <MoreStrollers
                            strollers={strollersOfThisBrand}
                            strollerPreviewPhotoMap={relatedStrollersPreviewPhotos}
                            photoObjectsMap={strollerVariantsPhotosMap}
                        />
                    </div>
                )}

                {!propagateSimilarProducts && (
                    <SimilarStrollers
                        className={styles.StrollersDetail__similarStrollers}
                        similars={similars}
                        comparison={comparison}
                        onComparisonToggle={onComparisonToggle}
                        onLoadMore={this.onLoadMoreSimilars}
                        loadingState={loadMoreSimilarsLoadingState}
                        hasMore={hasMoreSimilars}
                        count={countOfSimilars}
                        strollerPreviewPhotoMap={relatedStrollersPreviewPhotos}
                        photoObjectsMap={strollerVariantsPhotosMap}
                    />
                )}
            </div>
        );
    };

    private renderFailure = () => {
        const { errorMessage } = this.props;

        return (
            <Failure
                errorMessage={errorMessage}
                onRetry={this.load}
                backButtonUrl={strollersUrl}
                backButtonLabel={BACK_TO_STROLLERS_HOMEPAGE}
            />
        );
    };

    private onLoadMoreSimilars = () => {
        const { onLoadMoreSimilars, slug, similars } = this.props;
        onLoadMoreSimilars(slug, similars.length);
    };

    private load = () => {
        const { loadingState, slug, variantId, onComparisonLoad, onLoad } = this.props;

        // load only if the stroller is not yet fetched or currently not being loaded
        if (
            loadingState !== LoadingState.SUCCESS &&
            loadingState !== LoadingState.SUCCESS_ON_SERVER &&
            loadingState !== LoadingState.LOADING
        ) {
            onComparisonLoad();

            if (!variantId) {
                onLoad(slug);
            } else {
                onLoad(slug, variantId);
            }
        }
    };

    private getBreadcrumbItems = () => {
        const { stroller } = this.props;
        const breadcrumbLinks = [];
        breadcrumbLinks.push(
            <Link className={styles.BreadcrumbItem} key="strolleropedia" to={strollersUrl}>
                {STROLLER_STROLLEROPEDIA}
            </Link>,
        );
        breadcrumbLinks.push(
            stroller?.brand ? (
                <Link key="brand" to={url(strollersListUrl, { slug: stroller.brand.slug })}>
                    {stroller.brand.name}
                </Link>
            ) : (
                <>...</>
            ),
        );
        return breadcrumbLinks;
    };
}
